import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import api from '../lib/api';
import ContestCard from '../components/ContestCard';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';

const NONE = '__all__';

export default function Catalog() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [contests, setContests] = useState([]);
  const [filters, setFilters] = useState({ enti: [], categorie: [], regioni: [], titoli_studio: [], materie: [] });
  const [loading, setLoading] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);

  const params = useMemo(() => ({
    q: searchParams.get('q') || '',
    ente: searchParams.get('ente') || '',
    categoria: searchParams.get('categoria') || '',
    regione: searchParams.get('regione') || '',
    titolo_studio: searchParams.get('titolo_studio') || '',
    materia: searchParams.get('materia') || '',
    sort: searchParams.get('sort') || '',
  }), [searchParams]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value && value !== NONE) next.set(key, value); else next.delete(key);
    setSearchParams(next);
  };

  useEffect(() => {
    api.get('/contests/filters').then(({ data }) => setFilters(data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v)).toString();
    api.get(`/contests?${qs}`).then(({ data }) => setContests(data.contests)).catch(() => {})
      .finally(() => setLoading(false));
  }, [params]);

  const FilterSelect = ({ label, field, options, testId }) => (
    <div>
      <Label className="text-xs tracking-[0.1em] uppercase text-slate-500 font-semibold">{label}</Label>
      <Select value={params[field] || NONE} onValueChange={(v) => setParam(field, v)}>
        <SelectTrigger className="mt-1.5 h-11" data-testid={testId}><SelectValue placeholder="Tutti" /></SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>Tutti</SelectItem>
          {options.filter(Boolean).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  const FilterPanel = (
    <div className="space-y-5" data-testid="catalog-filters">
      <FilterSelect label="Ente" field="ente" options={filters.enti} testId="filter-ente" />
      <FilterSelect label="Categoria" field="categoria" options={filters.categorie} testId="filter-categoria" />
      <FilterSelect label="Regione" field="regione" options={filters.regioni} testId="filter-regione" />
      <FilterSelect label="Titolo di studio" field="titolo_studio" options={filters.titoli_studio} testId="filter-titolo" />
      <FilterSelect label="Materia" field="materia" options={filters.materie} testId="filter-materia" />
      <FilterSelect label="Scadenza" field="sort" options={['scadenza', 'prezzo']} testId="filter-sort" />
      {(params.q || params.ente || params.categoria || params.regione || params.titolo_studio || params.materia) && (
        <Button variant="ghost" onClick={() => setSearchParams({})} data-testid="filter-reset"
          className="text-sm text-slate-500">
          <X size={14} className="mr-1" strokeWidth={1.5} /> Azzera filtri
        </Button>
      )}
    </div>
  );

  return (
    <main className="page-shell py-10 sm:py-14" data-testid="catalog-page">
      <div className="mb-9 max-w-3xl">
        <p className="eyebrow mb-3">Catalogo Essentials</p>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-[-0.04em] font-bold text-brand-dark" data-testid="catalog-title">
          Trova il percorso giusto per il tuo concorso
        </h1>
        <p className="text-slate-600 mt-3 text-lg">Cerca per profilo, ente o territorio. Poi guarda subito quanto materiale ti aspetta.</p>
      </div>

      <div className="flex gap-3 mb-8">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} strokeWidth={1.5} />
          <Input aria-label="Cerca concorso o ente"
            defaultValue={params.q}
            onKeyDown={(e) => e.key === 'Enter' && setParam('q', e.target.value)}
            placeholder="Cerca concorso o ente..."
            data-testid="catalog-search-input"
            className="h-14 pl-11 rounded-2xl bg-white text-base shadow-sm"
          />
        </div>
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" className="md:hidden h-14 rounded-2xl px-5" data-testid="catalog-filters-open">
              <SlidersHorizontal size={16} strokeWidth={1.5} className="mr-2" /> Filtri
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-80 overflow-y-auto">
            <h3 className="font-display font-semibold text-lg mb-6 mt-4">Filtra concorsi</h3>
            {FilterPanel}
          </SheetContent>
        </Sheet>
      </div>

      <div className="grid md:grid-cols-[240px_1fr] gap-8">
        <aside className="hidden md:block md:col-span-1">
          <div className="surface p-6 sticky top-24">
            <h3 className="font-display font-semibold text-slate-900 mb-5">Filtra</h3>
            {FilterPanel}
          </div>
        </aside>
        <div>
          {!loading && <p className="mb-5 text-sm font-semibold text-slate-500" aria-live="polite">{contests.length} {contests.length === 1 ? 'concorso trovato' : 'concorsi trovati'}</p>}
          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => <div key={i} className="h-72 rounded-2xl bg-slate-100 animate-pulse" />)}
            </div>
          ) : contests.length === 0 ? (
            <div className="text-center py-20 bg-white border border-slate-200 rounded-2xl" data-testid="catalog-empty">
              <p className="text-slate-500">Nessun concorso trovato con questi filtri.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="catalog-grid">
              {contests.map((c, i) => <ContestCard key={c.contest_id} contest={c} index={i} />)}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
